/**
 * `<ThinkingBox>` — the inline tool-log pane for the two-stage chat pipeline (PR 3 of #102).
 *
 * design: docs/plans/chat-researcher-writer.md §4.3 + §10 PR 3.
 *
 * Renders the researcher's live tool-call log (the same data `<LiveSearchLog>` shows on
 * the single-stage path) inside a 3-line-tall, smooth-scrolling container so the user
 * sees what the agent is actually doing without it taking over the chat surface.
 *
 * # State machine (driven by `streamingTurn`)
 *
 *  - `expanded` — the researcher is mid-loop OR the user manually re-expanded the
 *    collapsed box. New events scroll smoothly into view at the bottom; user can wheel
 *    up to inspect prior steps (auto-scroll pauses until they return to bottom).
 *  - `collapsing` — a `phase: 'synthesizing'` event arrived. The 3-line container
 *    animates to a single-line summary ("Researched 3 notes in 4s") over ~200ms; the
 *    writer's bubble starts streaming below.
 *  - `collapsed` — fully collapsed. The summary line is clickable; click re-expands.
 *
 * # Smooth-scroll
 *
 * `useSmoothScrollToBottom` runs a `requestAnimationFrame` lerp loop while events arrive:
 * each frame moves `scrollTop` 20% of the way toward the bottom target. With ~30 events
 * over a 5-second tool round, this looks continuous instead of jumpy. If the user scrolls
 * up (wheel or touch), the loop pauses; scrolling back to within 8 px of the bottom
 * resumes it (so the user can inspect mid-round and re-engage without a click).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChatToolCallView } from './chatTypes';

const THINKING_BOX_HEIGHT_PX = 64; // ~3 lines @ 12.5px font, 1.6 line-height
const COLLAPSE_ANIMATION_MS = 200;
const SCROLL_BOTTOM_THRESHOLD_PX = 8;
const SCROLL_LERP_FACTOR = 0.2;

/** Props the parent passes — derived from `StreamingTurn`. */
export interface ThinkingBoxProps {
  /** The tool calls so far this turn, in order. */
  toolCalls: ChatToolCallView[];
  /** Whether the writer has begun synthesizing (collapses the box). */
  isSynthesizing: boolean;
  /**
   * The turn's elapsed time at the moment synthesizing started, in seconds — surfaced
   * in the collapsed summary ("Researched 3 notes in 4s"). When null, the parent has
   * not yet observed a `synthesizing` phase; the summary just shows the note count.
   */
  researchElapsedSeconds: number | null;
}

/**
 * Smooth-scroll the bottom of a scroll container into view as long as the user hasn't
 * scrolled away. Returns a ref to attach to the scrollable element. Idempotent and
 * cheap — the rAF loop only runs while `enabled` is true.
 */
export function useSmoothScrollToBottom(enabled: boolean): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  // Whether auto-scroll is currently engaged (paused on user scroll-up).
  const autoScrollRef = useRef(true);
  // The pending rAF id, so we can cancel on unmount or when `enabled` flips off.
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;

    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX) {
        // Back at the bottom — re-engage auto-scroll.
        autoScrollRef.current = true;
      } else if (distanceFromBottom > SCROLL_BOTTOM_THRESHOLD_PX * 4) {
        // Meaningfully scrolled away — disengage. We give the user a small dead zone
        // so a single accidental wheel tick doesn't pause forever.
        autoScrollRef.current = false;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return undefined;
    }
    const el = ref.current;
    if (el === null) return undefined;

    const tick = () => {
      rafRef.current = null;
      if (autoScrollRef.current && el.isConnected) {
        const target = el.scrollHeight - el.clientHeight;
        const delta = target - el.scrollTop;
        if (Math.abs(delta) > 0.5) {
          el.scrollTop = el.scrollTop + delta * SCROLL_LERP_FACTOR;
          rafRef.current = requestAnimationFrame(tick);
        }
      }
    };

    // Kick off a loop whenever `enabled` flips on — and continue while events arrive
    // (each render schedules a fresh frame via the effect below).
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled]);

  // Schedule a single follow-up frame on each commit while enabled — handles the
  // "content grew but no scroll event fires until we move scrollTop" case.
  useEffect(() => {
    if (!enabled || rafRef.current !== null) return;
    const el = ref.current;
    if (el === null) return;
    const target = el.scrollHeight - el.clientHeight;
    if (target - el.scrollTop > 0.5 && autoScrollRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (autoScrollRef.current) {
          const t = el.scrollHeight - el.clientHeight;
          const d = t - el.scrollTop;
          el.scrollTop = el.scrollTop + d * SCROLL_LERP_FACTOR;
        }
      });
    }
  });

  return ref;
}

/** Count the unique notes mentioned across all tool result summaries — best-effort,
 *  drives the collapsed-line summary. We surface tool count if no note-ish words. */
function summarizeForCollapsedLine(
  toolCalls: ChatToolCallView[],
  elapsedSeconds: number | null,
): string {
  const done = toolCalls.filter((tc) => tc.status === 'ok' || tc.status === 'error');
  const toolCount = done.length;
  const noteIds = new Set<string>();
  for (const tc of done) {
    const text = tc.resultSummary ?? '';
    // Cheap UUID matcher — same shape the backend uses.
    const matches = text.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi);
    if (matches !== null) {
      for (const m of matches) noteIds.add(m.toLowerCase());
    }
  }
  const elapsed = elapsedSeconds !== null ? ` in ${elapsedSeconds}s` : '';
  if (noteIds.size > 0) {
    const noun = noteIds.size === 1 ? 'note' : 'notes';
    return `Researched ${noteIds.size} ${noun}${elapsed}`;
  }
  if (toolCount > 0) {
    const noun = toolCount === 1 ? 'step' : 'steps';
    return `Ran ${toolCount} research ${noun}${elapsed}`;
  }
  // Shouldn't happen — synthesizing without any tools is the empty-research degraded
  // path, but render something sensible just in case.
  return `Synthesizing answer${elapsed}`;
}

/**
 * The inline thinking pane — wraps the tool-call log in a 3-line scrolling container
 * with collapse-on-synthesizing animation + click-to-re-expand.
 */
export function ThinkingBox({
  toolCalls,
  isSynthesizing,
  researchElapsedSeconds,
}: ThinkingBoxProps): JSX.Element {
  // Manual user-override: clicking the collapsed line forces back into expanded view
  // even though the writer is still streaming. The parent's state (toolCalls) is the
  // source of truth for content; this is purely a presentation toggle.
  const [userExpanded, setUserExpanded] = useState(false);
  // Track whether we're animating the collapse transition.
  const [isAnimating, setIsAnimating] = useState(false);
  const previousSynthesizingRef = useRef(false);
  useEffect(() => {
    if (isSynthesizing && !previousSynthesizingRef.current) {
      // Transition: research → synthesizing. Play the collapse animation.
      setIsAnimating(true);
      const t = window.setTimeout(() => setIsAnimating(false), COLLAPSE_ANIMATION_MS);
      previousSynthesizingRef.current = true;
      return () => window.clearTimeout(t);
    }
    if (!isSynthesizing) {
      previousSynthesizingRef.current = false;
    }
    return undefined;
  }, [isSynthesizing]);

  // Effective collapsed state — only collapse if synthesizing AND the user hasn't
  // chosen to re-expand.
  const collapsed = isSynthesizing && !userExpanded;

  const smoothScrollEnabled = !collapsed;
  const scrollRef = useSmoothScrollToBottom(smoothScrollEnabled);

  const summaryLine = useMemo(
    () => summarizeForCollapsedLine(toolCalls, researchElapsedSeconds),
    [toolCalls, researchElapsedSeconds],
  );

  if (toolCalls.length === 0 && !isSynthesizing) {
    // No content yet — render nothing (the parent shows the pulsing "thinking…" line
    // until the first tool call arrives).
    return <></>;
  }

  return (
    <div
      className={
        'chat-thinking-box' +
        (collapsed ? ' chat-thinking-box--collapsed' : '') +
        (isAnimating ? ' chat-thinking-box--animating' : '')
      }
      data-testid="chat-thinking-box"
    >
      {collapsed ? (
        <button
          type="button"
          className="chat-thinking-box__summary"
          onClick={() => setUserExpanded(true)}
          aria-label="Show the research details"
          aria-expanded={false}
        >
          <span className="chat-thinking-box__summary-mark">▸</span> {summaryLine}
        </button>
      ) : (
        <>
          <div
            ref={scrollRef}
            className="chat-thinking-box__scroll"
            style={{ maxHeight: `${THINKING_BOX_HEIGHT_PX}px` }}
            data-testid="chat-thinking-box-scroll"
          >
            <ThinkingBoxLog toolCalls={toolCalls} />
          </div>
          {isSynthesizing && userExpanded && (
            <button
              type="button"
              className="chat-thinking-box__collapse"
              onClick={() => setUserExpanded(false)}
              aria-label="Collapse the research details"
            >
              ▴ collapse
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** The actual log rows — same shape as the single-stage `<LiveSearchLog>` but
 *  rendered inside the bounded ThinkingBox container. */
function ThinkingBoxLog({ toolCalls }: { toolCalls: ChatToolCallView[] }): JSX.Element {
  return (
    <div className="chat-thinking-box__log" data-testid="chat-thinking-box-log">
      {toolCalls.map((tc) => {
        const args =
          tc.argsSummary && tc.argsSummary !== '(no arguments)' ? ` ${tc.argsSummary}` : '';
        return (
          <div key={tc.callId} className="chat-thinking-box__call">
            <div className="chat-thinking-box__cmd">
              <span className="chat-thinking-box__mark">→</span> {tc.toolName}
              {args}
            </div>
            {tc.innerTools !== null && tc.innerTools.length > 0 && (
              <div className="chat-thinking-box__via">↳ via {tc.innerTools.join(' · ')}</div>
            )}
            {tc.status === 'running' ? (
              <div className="chat-thinking-box__result chat-thinking-box__result--running">
                ↳ running…
              </div>
            ) : (
              tc.resultSummary !== null && (
                <div
                  className={`chat-thinking-box__result chat-thinking-box__result--${tc.status}`}
                >
                  {tc.status === 'error' ? '✗ ' : '✓ '}
                  {tc.resultSummary}
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
